import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { useProductCatalog } from './useProductCatalog';
import { formatDose } from '../utils/formatDose';
import { langModules } from '../i18n';
import en from '../i18n/en';
import type { Product, TranslatedField } from '../types/product';

/**
 * `catalog` is the product list search: every shown product attribute except
 * indications, applications, marketing claims, partner, appearance, shelf life,
 * MOQ, category, origin and the product website, which the customer excluded
 * because they flooded the results (VIVATISPDB-520). The price stays out as it
 * is hidden by default, URLs stay out like the product website. `quick` is the navbar
 * search and only knows product names, article numbers and the partner
 * (VIVATISPDB-521).
 */
export type SearchScope = 'catalog' | 'quick';

type RichTextField = Exclude<TranslatedField, 'name'>;

const BLOCK_BOUNDARY = /<\/?(?:p|div|br|li|ul|ol|h[1-6]|blockquote|section|article|table|tr|td|th)\b/gi;

/**
 * Strips the rich-text markup so neither tag names nor attribute values become
 * searchable and the field length stays honest for Fuse's score normalisation.
 * Block elements get a space so their words stay apart, inline ones do not so a
 * partially bolded word survives as one word.
 */
function toPlainText(html: string | null | undefined): string {
  if (!html) return '';
  const stripped = DOMPurify.sanitize(html.replace(BLOCK_BOUNDARY, ' $&'), {
    ALLOWED_TAGS: [],
    RETURN_DOM: true,
  });

  return (stripped.textContent ?? '').replace(/\s+/g, ' ').trim();
}

const plainText = (field: RichTextField | 'shortDescription') => (product: Product) =>
  toPlainText(product[field]);

const TRANSLATION_WEIGHT = 0.2;

/**
 * Fuse scores every entry of an array-valued key separately, so one key per
 * field covers all locales without hard-coding a language list. The active
 * locale also sits in the top-level keys, which is what weights it higher.
 */
const translations = (field: TranslatedField) => (product: Product) =>
  Object.values(product.allTranslations?.[field] ?? {});

const plainTranslations = (field: RichTextField) => (product: Product) =>
  translations(field)(product).map(toPlainText);

const RICH_TEXT_FIELDS = ['description', 'pitch', 'studies', 'bioactiveIngredients'] as const;

/** The dose as the detail page shows it, plus a plain hyphen so "100-200" finds "100–200 mg". */
const dose = (product: Product): string[] => {
  const formatted = formatDose(product);
  return formatted ? [formatted, formatted.replace('–', '-')] : [];
};

const NOVEL_FOOD_LABELS = [en, ...Object.values(langModules)].map(
  (strings) => strings.comparison.novelFoodStatuses as Record<string, string>,
);

/** The Novel Food status as the detail page shows it, in every UI language. */
const novelFood = (product: Product): string[] => {
  const status = product.novelFoodStatus;
  if (!status) return [];
  return [...new Set([status, ...NOVEL_FOOD_LABELS.map((labels) => labels[status] ?? status)])];
};

const CATALOG_KEYS: IFuseOptions<Product>['keys'] = [
  { name: 'name', weight: 3 },
  { name: 'articleNumber', weight: 2 },
  { name: 'description', weight: 1, getFn: plainText('description') },
  { name: 'pitch', weight: 1, getFn: plainText('pitch') },
  { name: 'shortDescription', weight: 1, getFn: plainText('shortDescription') },
  { name: 'studies', weight: 0.8, getFn: plainText('studies') },
  { name: 'bioactiveIngredients', weight: 0.8, getFn: plainText('bioactiveIngredients') },
  { name: 'dose', weight: 0.8, getFn: dose },
  { name: 'territories.name', weight: 0.5 },
  { name: 'clinicalStudies', weight: 0.5, getFn: (product) => String(product.clinicalStudies) },
  { name: 'novelFoodStatus', weight: 0.5, getFn: novelFood },
  { name: 'allTranslations.name', weight: TRANSLATION_WEIGHT, getFn: translations('name') },
  ...RICH_TEXT_FIELDS.map((field) => ({
    name: `allTranslations.${field}`,
    weight: TRANSLATION_WEIGHT,
    getFn: plainTranslations(field),
  })),
];

const SEARCH_KEYS: Record<SearchScope, IFuseOptions<Product>['keys']> = {
  catalog: CATALOG_KEYS,
  quick: [
    { name: 'name', weight: 3 },
    { name: 'articleNumber', weight: 2 },
    { name: 'partner.name', weight: 1.5 },
    { name: 'allTranslations.name', weight: TRANSLATION_WEIGHT, getFn: translations('name') },
  ],
};

/**
 * 0.16 is the widest window that still keeps the long rich-text fields honest.
 * Measured against the live catalog (132 products): at 0.17 "Malnutrition"
 * grows from its one real hit to five, at 0.14 the typo "Curcmin" stops
 * finding Curcumin at all. Both edges only appeared once the full body text
 * and every translation joined the index, which is why 0.3 no longer fits.
 */
const FUSE_OPTIONS: Omit<IFuseOptions<Product>, 'keys'> = {
  threshold: 0.16,
  ignoreLocation: true,
  includeScore: true,
};

/**
 * One Fuse index per catalog array reference and scope. React Query hands out
 * the same array until the catalog changes, so the index is built once per
 * catalog version and shared by every caller.
 */
const indexCache = new WeakMap<Product[], Partial<Record<SearchScope, Fuse<Product>>>>();

function getIndex(products: Product[], scope: SearchScope): Fuse<Product> {
  let indexes = indexCache.get(products);
  if (!indexes) {
    indexes = {};
    indexCache.set(products, indexes);
  }
  let index = indexes[scope];
  if (!index) {
    index = new Fuse(products, { ...FUSE_OPTIONS, keys: SEARCH_KEYS[scope] });
    indexes[scope] = index;
  }
  return index;
}

const NO_RESULTS: Product[] = [];

/**
 * Fuzzy full-text search over the cached catalog, ordered by relevance.
 * An empty term returns the input untouched so it can be chained with filters.
 */
export function searchProducts(
  products: Product[],
  term: string,
  scope: SearchScope = 'catalog',
): Product[] {
  const query = term.trim();
  if (!query) return products;
  return getIndex(products, scope).search(query).map((result) => result.item);
}

/**
 * React wrapper around {@link searchProducts}. An empty term yields no results.
 */
export function useFulltextSearch(term: string, scope: SearchScope = 'catalog') {
  const { data: catalog, isLoading } = useProductCatalog();

  const results = useMemo(
    () => (catalog && term.trim() ? searchProducts(catalog, term, scope) : NO_RESULTS),
    [catalog, term, scope],
  );

  return { results, isLoading };
}
